<template>
  <div class="custom-radio-list">
    <ul class="custom-radio-list__options" :class="customRadioListOptionsClass">
      <li
        v-for="(option, index) in options"
        :key="getOptionKey(option, index)"
        class="custom-radio-list__option"
        :class="customRadioListOptionClass"
        @click="handleOptionClick(option)"
      >
        <div class="custom-radio-list__label-wrap" :class="labelWrapClass">
          <span v-if="option.icon || $slots.icon" class="custom-radio-list__icon">
            <slot name="icon" :option="option" :index="index">
              <AppIcon v-if="option.icon" :icon="option.icon" />
            </slot>
          </span>

          <span v-if="option.image && showImages" class="custom-radio-list__image">
            <div class="custom-radio-list__image-container">
              <slot name="image" :option="option" :index="index">
                <AsyncImage :image="option.image" :show-images="showImages" />
              </slot>
            </div>
          </span>

          <span
            class="custom-radio-list__label custom-radio-list__label--primary"
            v-dompurify-html="getOptionLabel(option)"
          ></span>

          <span
            v-if="getOptionSecondary(option)"
            class="custom-radio-list__label custom-radio-list__label--secondary"
            :class="{ 'text-primary fw-bold': option.highlightSecondary }"
            v-dompurify-html="getOptionSecondary(option)"
          ></span>

          <span
            v-if="option.secondaryIcon || $slots.secondaryIcon"
            class="custom-radio-list__icon custom-radio-list__icon--right"
          >
            <slot name="secondaryIcon" :option="option" :index="index">
              <AppIcon v-if="option.secondaryIcon" :icon="option.secondaryIcon" />
            </slot>
          </span>
        </div>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import AsyncImage from '~conciergeUiCommons/components/AsyncImage.vue'

export interface RadioListOption {
  id?: string | number
  label: string
  value?: any
  secondary?: string
  icon?: string
  secondaryIcon?: string
  image?: any
  disabled?: boolean
  highlightSecondary?: boolean
  active?: boolean
  [key: string]: any
}

export default defineComponent({
  name: 'CustomRadioList',
  components: { AsyncImage },
  emits: ['option-selected'],
  props: {
    options: {
      type: Array as PropType<RadioListOption[]>,
      required: true,
      default: () => []
    },
    layout: {
      type: String as PropType<'vertical' | 'horizontal'>,
      default: 'vertical'
    },
    labelAlign: {
      type: String as PropType<'start' | 'center' | 'between'>,
      default: 'start'
    },
    showImages: {
      type: Boolean,
      default: false
    },
    activeValue: {
      type: [String, Number, Object],
      default: null
    },
    trackBy: {
      type: String,
      default: 'value'
    },
    labelBy: {
      type: String,
      default: 'label'
    },
    secondaryBy: {
      type: String,
      default: 'secondary'
    }
  },
  computed: {
    customRadioListOptionClass() {
      return {
        'custom-radio-list__option--active': this.isOptionActive(this.options[0]),
        'custom-radio-list__option--disabled': this.options[0].disabled
      }
    },
    customRadioListOptionsClass() {
      return `custom-radio-list__options--${this.layout}`
    },
    labelWrapClass() {
      return `custom-radio-list__label-wrap--${this.labelAlign}`
    }
  },
  methods: {
    getOptionKey(option: RadioListOption, index: number): string | number {
      return option.id ?? option[this.trackBy] ?? index
    },

    getOptionLabel(option: RadioListOption): string {
      return option[this.labelBy] ?? option.label ?? ''
    },

    getOptionSecondary(option: RadioListOption): string | null {
      return option[this.secondaryBy] ?? option.secondary ?? null
    },

    isOptionActive(option: RadioListOption): boolean {
      if (option.active !== undefined) {
        return option.active
      }
      if (this.activeValue !== null) {
        const optionValue = option[this.trackBy] ?? option.value ?? option
        return optionValue === this.activeValue
      }
      return false
    },

    handleOptionClick(option: RadioListOption): void {
      if (option.disabled) {
        return
      }

      const optionValue = option[this.trackBy] ?? option.value ?? option
      this.$emit('option-selected', optionValue, option)
    }
  }
})
</script>
